import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Button,
  TextField,
  Typography,
  CircularProgress,
  IconButton,
  InputAdornment,
} from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { useForm, Controller } from 'react-hook-form';
import { toast } from 'react-toastify';
import apiService from '../../../services/api';

interface Props {
  open: boolean;
  onClose: () => void;
}

interface EmailFormValues {
  email: string;
}

interface OtpFormValues {
  otp: string;
}

interface ResetFormValues {
  password: string;
  confirmPassword: string;
}

const ForgotPasswordModal: React.FC<Props> = ({ open, onClose }) => {
  const [step, setStep] = useState<'email' | 'otp' | 'reset'>('email');
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [accessToken, setAccessToken] = useState('');

  const [seconds, setSeconds] = useState(60);
  const [canResend, setCanResend] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);

  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  // FORMS
  const emailForm = useForm<EmailFormValues>({ defaultValues: { email: '' } });
  const otpForm = useForm<OtpFormValues>({ defaultValues: { otp: '' } });
  const resetForm = useForm<ResetFormValues>({
    defaultValues: { password: '', confirmPassword: '' },
  });

  const passwordValue = resetForm.watch('password');

  //////////otp timer///////////
  // useEffect(() => {
  //   if (step !== "otp") return;

  //   setSeconds(60);
  //   setCanResend(false);

  //   const interval = setInterval(() => {
  //     setSeconds((prev) => {
  //       if (prev === 1) {
  //         setCanResend(true);
  //         clearInterval(interval);
  //       }
  //       return prev - 1;
  //     });
  //   }, 1000);

  //   return () => clearInterval(interval);
  // }, [step]);

  useEffect(() => {
    if (step !== 'otp') return;

    if (seconds === 60) {
      setCanResend(false);
    }

    const interval = setInterval(() => {
      setSeconds(prev => {
        if (prev === 1) {
          setCanResend(true);
          clearInterval(interval);
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(interval);
  }, [step, seconds]);

  ///////////// SEND OTP//////////////
  const sendOtp = async (data: EmailFormValues) => {
    setLoading(true);
    try {
      const res = await apiService.sendOtpToEmail({ email: data.email });
      setEmail(data.email);
      toast.success(res.message || 'OTP sent successfully');
      setStep('otp');
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'Failed to send OTP');
    } finally {
      setLoading(false);
    }
  };

  ///////////////////resend otp////////////////
  const resendOtp = async () => {
    if (!canResend || resendLoading) return;
    setResendLoading(true);
    try {
      await apiService.resendOtp({ email });
      setSeconds(60);
      toast.success('OTP resent');
      setCanResend(false);
    } catch {
      toast.error('Failed to resend OTP');
    } finally {
      setResendLoading(false);
    }
  };

  //////////////////////verify otp/////////////////////
  const verifyOtp = async (data: OtpFormValues) => {
    setLoading(true);
    try {
      const res = await apiService.verifyOtp({
        email,
        otpCode: data.otp,
        otpType: 'password_reset',
      });

      setAccessToken(res.access_token);
      toast.success(res.message || 'OTP verified');
      setStep('reset');
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'Invalid OTP');
    } finally {
      setLoading(false);
    }
  };

  /////////////////////reset password////////////////////
  const resetPassword = async (data: ResetFormValues) => {
    setLoading(true);
    try {
      await apiService.passwordReset({
        email,
        resetToken: accessToken,
        newPassword: data.password,
      });

      toast.success('Password reset successfully');
      setStep('email');
      setEmail('');
      setAccessToken('');
      onClose();
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'Failed to reset password');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ textAlign: 'center', fontWeight: 600 }}>
        {step === 'email' && 'Forgot Password'}
        {step === 'otp' && 'Verify OTP'}
        {step === 'reset' && 'Reset Password'}
      </DialogTitle>

      <DialogContent>
        {/* STEP 1 - EMAIL */}
        {step === 'email' && (
          <form onSubmit={emailForm.handleSubmit(sendOtp)}>
            <Controller
              name="email"
              control={emailForm.control}
              rules={{
                required: 'Email is required',
                pattern: {
                  value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                  message: 'Invalid email format',
                },
              }}
              render={({ field, fieldState }) => (
                <TextField
                  {...field}
                  fullWidth
                  margin="normal"
                  label="Email Address"
                  error={!!fieldState.error}
                  helperText={fieldState.error?.message}
                />
              )}
            />

            <Button
              fullWidth
              type="submit"
              variant="contained"
              sx={{ mt: 2 }}
              disabled={loading}
              startIcon={loading ? <CircularProgress size={20} /> : undefined}
            >
              Send OTP
            </Button>
          </form>
        )}

        {/* STEP 2 - OTP */}
        {step === 'otp' && (
          <form onSubmit={otpForm.handleSubmit(verifyOtp)}>
            <Typography sx={{ mb: 1 }}>
              OTP sent to <b>{email}</b>
            </Typography>

            <Controller
              name="otp"
              control={otpForm.control}
              rules={{ required: 'OTP is required' }}
              render={({ field, fieldState }) => (
                <TextField
                  {...field}
                  fullWidth
                  margin="normal"
                  label="Enter OTP"
                  error={!!fieldState.error}
                  helperText={fieldState.error?.message}
                />
              )}
            />

            {/* RESEND BUTTON + TIMER */}
            <Button
              fullWidth
              sx={{ mt: 1 }}
              disabled={!canResend || resendLoading}
              onClick={resendOtp}
              startIcon={
                resendLoading ? <CircularProgress size={20} /> : undefined
              }
            >
              {resendLoading
                ? 'Resending...'
                : canResend
                  ? 'Resend OTP'
                  : `Resend in ${seconds}s`}
            </Button>

            <Button fullWidth type="submit" variant="contained" sx={{ mt: 2 }}>
              Verify OTP
            </Button>
          </form>
        )}

        {/* STEP 3 - RESET PASSWORD */}
        {step === 'reset' && (
          <form onSubmit={resetForm.handleSubmit(resetPassword)}>
            {/* PASSWORD */}
            <Controller
              name="password"
              control={resetForm.control}
              rules={{
                required: 'Password is required',
                minLength: { value: 6, message: 'Min 6 characters' },
              }}
              render={({ field, fieldState }) => (
                <TextField
                  {...field}
                  type={showPassword ? 'text' : 'password'}
                  fullWidth
                  margin="normal"
                  label="New Password"
                  error={!!fieldState.error}
                  helperText={fieldState.error?.message}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => setShowPassword(!showPassword)}
                        >
                          {showPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              )}
            />

            {/* CONFIRM PASSWORD */}
            <Controller
              name="confirmPassword"
              control={resetForm.control}
              rules={{
                required: 'Confirm password is required',
                validate: value =>
                  value === passwordValue || 'Passwords do not match',
              }}
              render={({ field, fieldState }) => (
                <TextField
                  {...field}
                  type={showConfirm ? 'text' : 'password'}
                  fullWidth
                  margin="normal"
                  label="Confirm Password"
                  error={!!fieldState.error}
                  helperText={fieldState.error?.message}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => setShowConfirm(!showConfirm)}
                        >
                          {showConfirm ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              )}
            />

            <Button
              fullWidth
              type="submit"
              variant="contained"
              disabled={loading}
              sx={{ mt: 2 }}
              startIcon={loading ? <CircularProgress size={20} /> : undefined}
            >
              Reset Password
            </Button>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default ForgotPasswordModal;
